import { useState, useEffect, useCallback, useRef } from "react";
import axios from "../../Assets/Lib/axios";
import {
  Page,
  Card,
  Button,
  DataTable,
  Select,
  TextField,
  InlineStack,
  InlineGrid,
  Modal,
  Text,
  PageActions,
  FormLayout,
  DropZone,
  Image,
  Toast,
  Thumbnail,
  Spinner,
  ButtonGroup,
} from "@shopify/polaris";
import { DeleteIcon, EditIcon } from "@shopify/polaris-icons";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../Components/LoadingScreen";
import emptyListImage from "../../Assets/Images/emptyList.svg";

export default function Metrics() {
  const { id } = useParams();
  const [competitors, setCompetitors] = useState([]);
  const [metrics, setMetrics] = useState(null); // {name, type}
  const [newMetricName, setNewMetricName] = useState("");
  const [newMetricType, setNewMetricType] = useState("text");
  const [showMetricModal, setShowMetricModal] = useState(false);
  const [competitorOptions, setCompetitorOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [isEditable, setIsEditable] = useState(false); // toggle editable/preview

  // For add competitor modal
  const [showCompetitorModal, setShowCompetitorModal] = useState(false);
  const [selectedCompetitor, setSelectedCompetitor] = useState("");

  const capitalizeFirstLetter = (str) =>
    str.charAt(0).toUpperCase() + str.slice(1);

  // Fetch competitors
  useEffect(() => {
    const fetchCompetitorsOptions = async () => {
      try {
        const { data } = await axios.get(`/competitors?all`);
        const options = [
          { label: "Select competitor", value: "" },
          ...data.data.map((c) => ({
            label: c.business_name,
            value: c.id.toString(),
            logo: c.logo || "",
          })),
        ];
        setCompetitorOptions(options);
      } catch (error) {
        console.error("Error fetching competitors:", error);
      }
    };
    fetchCompetitorsOptions();
  }, []);
  const [title, setTitle] = useState("");
  useEffect(() => {
    const fetchBenchmarkData = async () => {
      try {
        const { data } = await axios.get(`/parent/${id}/benchmarks`);
        const res = data.data;
        console.log("data-", data);
        if (res && res.length > 0) {
          const benchmarks = res;

          const metrics = benchmarks.map((b) => ({
            name: b.metric_name,
            type: b.metric_data_type,
          }));

          const competitorMap = {};
          benchmarks.forEach((b) => {
            b.competitors.forEach((c, compIdx) => {
              const compId = c.id.toString();

              if (!competitorMap[compId]) {
                competitorMap[compId] = { competitor: compId, metrics: {} };
              }

              if (b.metric_data_type === "image") {
                // metric_value (URLs) and file_keys (S3 keys)
                const urls = Array.isArray(c.metric_value)
                  ? c.metric_value
                  : [];
                const fileKeys = Array.isArray(c.file_keys) ? c.file_keys : [];

                competitorMap[compId].metrics[b.metric_name] = fileKeys;
                competitorMap[compId].metrics[`${b.metric_name}_previews`] =
                  urls;

                // ✅ Initialize keysRef with these file keys for saving later
                if (!keysRef.current[compId]) keysRef.current[compId] = {};
                keysRef.current[compId][b.metric_name] = [...fileKeys];
              } else {
                competitorMap[compId].metrics[b.metric_name] = c.metric_value;
              }
            });
          });

          console.log("metrics=", metrics);
          setMetrics(metrics);
          setCompetitors(Object.values(competitorMap));
        } else {
          setMetrics([]);
        }
        setTitle(data.title);
      } catch (error) {
        console.error("Error fetching benchmark data:", error);
      }
    };

    fetchBenchmarkData();
  }, []);

  const handleAddCompetitor = () => {
    setSelectedCompetitor("");
    setShowCompetitorModal(true);
  };

  const handleConfirmAddCompetitor = () => {
    if (!selectedCompetitor) return;

    const newMetrics = Object.fromEntries(metrics.map((m) => [m.name, ""]));
    setCompetitors((prev) => [
      ...prev,
      { competitor: selectedCompetitor, metrics: newMetrics },
    ]);

    setShowCompetitorModal(false);
  };

  const handleDeleteCompetitor = (index) =>
    setCompetitors((prev) => prev.filter((_, i) => i !== index));
  const [metricError, setMetricError] = useState("");

  // const handleAddMetric = () => {
  //   const key = newMetricName.trim();
  //   if (!key) {
  //     setMetricError("This field is required");
  //     return;
  //   }

  //   const safeKey = key.replace(/\s+/g, "_"); // ✅ avoid empty/space-only names
  //   if (metrics.some((m) => m.name === safeKey)) {
  //     setMetricError("Metric name already exists");
  //     return;
  //   }

  //   const newMetricObj = { name: safeKey, type: newMetricType };
  //   setMetrics((prev) => [...prev, newMetricObj]);
  //   setCompetitors((prev) =>
  //     prev.map((c) => ({ ...c, metrics: { ...c.metrics, [safeKey]: "" } }))
  //   );
  //   setNewMetricName("");
  //   setNewMetricType("text");
  //   setShowMetricModal(false);
  //   setMetricError("");
  // };
  const handleAddMetric = () => {
    const key = newMetricName.trim();
    if (!key) {
      setMetricError("This field is required");
      return;
    }

    // ✅ Keep spaces as-is (no replacement)
    if (metrics.some((m) => m.name === key)) {
      setMetricError("Metric name already exists");
      return;
    }

    const newMetricObj = { name: key, type: newMetricType };
    setMetrics((prev) => [...prev, newMetricObj]);
    setCompetitors((prev) =>
      prev.map((c) => ({ ...c, metrics: { ...c.metrics, [key]: "" } }))
    );

    setNewMetricName("");
    setNewMetricType("text");
    setShowMetricModal(false);
    setMetricError("");
  };

  const handleDeleteMetric = (metricName) => {
    setMetrics((prev) => prev.filter((m) => m.name !== metricName));
    setCompetitors((prev) =>
      prev.map((c) => {
        const { [metricName]: _, ...rest } = c.metrics;
        return { ...c, metrics: rest };
      })
    );
  };

  const handleSelectChange = (value, index) => {
    const updated = [...competitors];
    updated[index].competitor = value;
    setCompetitors(updated);
  };

  const handleMetricChange = (metricName, value, index) => {
    const updated = [...competitors];
    updated[index].metrics[metricName] = value;
    setCompetitors(updated);
  };
  const handleSave = async () => {
    setLoading(true);
    try {
      const benchmarks = metrics.map((metric) => ({
        metric_name: metric.name.replace(/_/g, " "),
        metric_data_type: metric.type.toLowerCase(),
        competitors: competitors
          .filter((c) => c.competitor)
          .map((c, index) => ({
            id: Number(c.competitor),
            metric_value:
              metric.type.toLowerCase() === "image"
                ? keysRef.current[c.competitor]?.[metric.name] || [] // ✅ use keys saved per competitor ID
                : c.metrics[metric.name],
          })),
      }));

      await axios.post(`/parent/${id}/benchmarks`, { benchmarks });
      setToastContent("Changes have been saved successfully");
      toggleActive();
    } catch (error) {
      console.error("Error saving benchmark:", error);
    } finally {
      setLoading(false);
    }
  };

  // const handleSave = async () => {
  //   setLoading(true);
  //   try {
  //     const benchmarks = metrics.map((metric) => ({
  //       metric_name: metric.name,
  //       metric_data_type: metric.type.toLowerCase(),
  //       competitors: competitors
  //         .filter((c) => c.competitor)
  //         .map((c) => ({
  //           id: Number(c.competitor),
  //           metric_value: c.metrics[metric.name],
  //         })),
  //     }));

  //     await axios.post(`/parent/${id}/benchmarks`, { benchmarks });
  //     setLoading(false);
  //   } catch (error) {
  //     console.error("Error saving benchmark:", error);
  //     setLoading(false);
  //   }
  // };

  const [isImageUploading, setIsImageUploading] = useState(false);
  const validImageTypes = [
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/webp",
  ];

  const [files, setFiles] = useState();
  const handleDropZone = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) => setFiles(acceptedFiles[0]),
    []
  );

  const fileUpload = !files && <DropZone.FileUpload />;
  const uploadedFiles = files && (
    <InlineStack gap="200" align="start" blockAlign="center">
      <div className="maxtextwidth">
        {files.name}{" "}
        <Text variant="bodySm" as="p">
          {files.size} bytes
        </Text>
      </div>
    </InlineStack>
  );
  const [imageError, setImageError] = useState("");
  const imageKey = useRef("");
  const keysRef = useRef({});
  const sendImages = async (files, metricName, index) => {
    setIsImageUploading(true);

    try {
      // Upload each file and get key + preview
      const uploadPromises = files.map(async (file) => {
        const form_data = new FormData();
        form_data.append("entity_type", "metric_value");
        form_data.append("image", file);

        const { data } = await axios.post(`/s3/save-file`, form_data);

        return {
          key: data.data.file_key,
          preview: URL.createObjectURL(file),
        };
      });

      const uploaded = await Promise.all(uploadPromises);

      // ✅ Use competitorId (more stable than array index)
      const competitorId = competitors[index].competitor;

      // Initialize nested structure if missing
      if (!keysRef.current[competitorId]) keysRef.current[competitorId] = {};
      if (!keysRef.current[competitorId][metricName])
        keysRef.current[competitorId][metricName] = [];

      // Add new uploaded file keys
      keysRef.current[competitorId][metricName].push(
        ...uploaded.map((u) => u.key)
      );

      // ✅ Update state for UI preview
      setCompetitors((prev) => {
        const updated = [...prev];
        const prevPreviews =
          updated[index].metrics[`${metricName}_previews`] || [];

        updated[index].metrics[`${metricName}_previews`] = [
          ...prevPreviews,
          ...uploaded.map((u) => u.preview),
        ];

        return updated;
      });
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setIsImageUploading(false);
    }
  };
  const removeImage = (metricName, competitorIndex, imageIndex) => {
    const competitorId = competitors[competitorIndex].competitor;

    // ✅ Remove from keysRef using competitorId
    if (
      keysRef.current[competitorId] &&
      keysRef.current[competitorId][metricName]
    ) {
      keysRef.current[competitorId][metricName].splice(imageIndex, 1);
    }

    // ✅ Update state preview for UI
    setCompetitors((prev) => {
      const updated = [...prev];
      const previews =
        updated[competitorIndex].metrics[`${metricName}_previews`] || [];
      previews.splice(imageIndex, 1);
      updated[competitorIndex].metrics[`${metricName}_previews`] = [
        ...previews,
      ];
      return updated;
    });
  };

  // Build table headings
  const headings = metrics
    ? [
        "Competitor",
        ...metrics?.map((m, idx) => (
          <InlineStack
            key={m.name || `metric-${idx}`}
            align="center"
            spacing="extraTight"
          >
            <Text>{m.name}</Text>
            {isEditable && (
              <InlineStack>
                <Button
                  icon={EditIcon}
                  tone="critical"
                  onClick={() => {
                    setEditingMetricName(m.name);
                    setEditingMetricNewName(m.name);
                    setShowEditMetricModal(true);
                  }}
                />
                <Button
                  tone="critical"
                  icon={DeleteIcon}
                  onClick={() => handleDeleteMetric(m.name)}
                />
              </InlineStack>
            )}
          </InlineStack>
        )),
        isEditable ? "Actions" : "",
      ]
    : [];

  // Build table rows
  const rows = competitors.map((item, index) => [
    isEditable ? (
      <Select
        key={`competitor-${index}`}
        options={competitorOptions}
        value={item.competitor}
        onChange={(value) => handleSelectChange(value, index)}
        placeholder="Please select"
      />
    ) : (
      <div className="rowDiv">
        {(() => {
          const comp =
            competitorOptions.find((o) => o.value === item.competitor) || {};
          return (
            <>
              <Thumbnail
                source={
                  comp.logo ||
                  "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png"
                }
                size="extraSmall"
                alt={`${comp.label || "placeholder"} logo`}
              />
              <div className="ms-2">
                <Text as="span" variant="bodyMd">
                  {comp.label}
                </Text>
              </div>
            </>
          );
        })()}
      </div>
      // <Text key={`text-${index}`}>
      //   {competitorOptions.find((o) => o.value === item.competitor)?.label ||
      //     "N/A"}
      // </Text>
    ),
    ...metrics.map((metric, mIdx) => {
      const keyBase = `${index}-${metric.name || mIdx}`;
      const type = metric.type;
      const value = item.metrics[metric.name] || "";
      if (!isEditable) {
        if (type === "image") {
          const previews = item.metrics[`${metric.name}_previews`] || [];

          if (previews.length === 0) {
            return (
              <div
                key={`image-${keyBase}`}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Thumbnail
                  size="small"
                  source="https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png"
                />
              </div>
            );
          }

          return (
            <div
              key={`image-${keyBase}`}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              {previews.map((src, i) => (
                <Thumbnail
                  key={`${keyBase}-preview-${i}`}
                  size="small"
                  source={src}
                  alt={`Image ${i + 1}`}
                  onClick={() => window.open(src, "_blank")}
                />
              ))}
            </div>
          );
        }

        return (
          <div style={{ textAlign: "center" }} key={`text-${keyBase}`}>
            <Text>{value}</Text>
          </div>
        );
      }
      // if (!isEditable)
      //   return (
      //     <div style={{ textAlign: "center" }}>
      //       <Text key={`${index}-${metric.name}`}>{value}</Text>
      //     </div>
      //   );

      // editable field keys:
      if (type === "text" || type === "numerical" || type === "percentage") {
        return (
          <TextField
            key={`field-${keyBase}`}
            value={value}
            onChange={(v) => handleMetricChange(metric.name, v, index)}
            labelHidden
            type={
              type === "numerical" || type === "percentage" ? "number" : "text"
            }
            suffix={type === "percentage" ? "%" : undefined}
          />
        );
      } else if (type === "boolean") {
        return (
          <Select
            key={`bool-${keyBase}`}
            options={[
              { label: "False", value: "false" },
              { label: "True", value: "true" },
            ]}
            value={value || "false"}
            onChange={(v) => handleMetricChange(metric.name, v, index)}
          />
        );
      } else if (type === "image") {
        return (
          <div
            key={`img-${keyBase}`}
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "8px",
              justifyContent: "center",
            }}
          >
            {(item.metrics[`${metric.name}_previews`] || []).map((src, i) => (
              <div
                key={`${keyBase}-thumb-${i}`}
                style={{
                  position: "relative",
                  width: "60px",
                  height: "60px",
                }}
              >
                <Thumbnail size="small" source={src} alt={`Image ${i + 1}`} />
                {isEditable && (
                  <Button
                    icon={DeleteIcon}
                    tone="critical"
                    variant="plain"
                    onClick={() => removeImage(metric.name, index, i)}
                    accessibilityLabel="Delete image"
                    style={{
                      position: "absolute",
                      top: "-8px",
                      right: "-8px",
                      backgroundColor: "white",
                      borderRadius: "50%",
                      boxShadow: "0 0 4px rgba(0,0,0,0.2)",
                      padding: "0",
                      minHeight: "22px",
                      minWidth: "22px",
                    }}
                  />
                )}
              </div>
            ))}

            {isEditable && (
              <DropZone
                allowMultiple
                onDrop={(accepted) => sendImages(accepted, metric.name, index)}
                style={{ width: "60px", height: "60px" }}
              >
                <DropZone.FileUpload />
              </DropZone>
            )}
          </div>
          // <div
          //   key={`img-${keyBase}`}
          //   className="rowDiv"
          //   style={{
          //     display: "flex",
          //     justifyContent: "center",
          //     alignItems: "center",
          //     gap: "8px",
          //   }}
          // >
          //   <Thumbnail
          //     size="small"
          //     source={
          //       item.metrics[`${metric.name}_preview`] ||
          //       (item.metrics[metric.name]
          //         ? `${item.metrics[metric.name]}`
          //         : "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png")
          //     }
          //   />

          //   {isEditable && (
          //     <div style={{ textAlign: "center", width: 40, height: 40 }}>
          //       <DropZone
          //         onDrop={(accepted) => sendImage(accepted, metric.name, index)}
          //         allowMultiple={false}
          //       >
          //         <DropZone.FileUpload />
          //       </DropZone>
          //     </div>
          //   )}
          // </div>
        );
      }
      return null;
    }),
    isEditable ? (
      <Button
        key={`delete-${index}`}
        tone="critical"
        onClick={() => handleDeleteCompetitor(index)}
        icon={DeleteIcon}
      />
    ) : null,
  ]);
  const navigate = useNavigate();

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive(!active), []);
  const [toastContent, setToastContent] = useState("");
  const [showEditMetricModal, setShowEditMetricModal] = useState(false);
  const [editingMetricName, setEditingMetricName] = useState("");
  const [editingMetricNewName, setEditingMetricNewName] = useState("");
  const handleRenameMetric = (oldName, newName) => {
    // Update metrics array
    setMetrics((prev) =>
      prev.map((m) => (m.name === oldName ? { ...m, name: newName } : m))
    );

    // Update competitor metrics keys
    setCompetitors((prev) =>
      prev.map((c) => {
        const updatedMetrics = { ...c.metrics };

        if (updatedMetrics[oldName] !== undefined) {
          updatedMetrics[newName] = updatedMetrics[oldName];
          delete updatedMetrics[oldName];
        }

        // For image previews
        if (updatedMetrics[`${oldName}_previews`]) {
          updatedMetrics[`${newName}_previews`] =
            updatedMetrics[`${oldName}_previews`];
          delete updatedMetrics[`${oldName}_previews`];
        }

        return { ...c, metrics: updatedMetrics };
      })
    );

    // Update keysRef (for images)
    Object.keys(keysRef.current).forEach((compId) => {
      if (keysRef.current[compId][oldName] !== undefined) {
        keysRef.current[compId][newName] = keysRef.current[compId][oldName];
        delete keysRef.current[compId][oldName];
      }
    });
  };

  return metrics ? (
    <Page
      fullWidth
      title={`Competitors Comparison - ${title}`}
      primaryAction={
        <Button variant="primary" onClick={() => setIsEditable(!isEditable)}>
          {isEditable ? "Switch to Preview" : "Switch to Edit"}
        </Button>
      }
      backAction={{ onAction: () => navigate(`/admin/benchmarks`) }}
    >
      <Card padding={0}>
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            {/* Competitors Comparison */}
          </Text>
          <InlineGrid gap="200" columns="auto auto auto auto">
            {isEditable && (
              <div className="mt-3">
                <ButtonGroup>
                  <Button onClick={handleAddCompetitor} variant="primary">
                    Add Competitor
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => setShowMetricModal(true)}
                  >
                    Add Metric
                  </Button>
                </ButtonGroup>
              </div>
            )}
          </InlineGrid>
        </InlineStack>

        {competitors.length === 0 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "middle",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <div style={{ width: "25%" }}>
              <img src={emptyListImage}></img>
            </div>
            <div className="mb-3">
              <Text variant="headingLg" as="h5" color="subdued">
                No results found
              </Text>
            </div>
            <br />
          </div>
        ) : (
          <DataTable
            columnContentTypes={Array(headings.length).fill("numeric")}
            headings={headings}
            rows={rows}
            verticalAlign="middle"
            increasedTableDensity
          />
        )}
      </Card>
      <Modal
        open={showEditMetricModal}
        onClose={() => setShowEditMetricModal(false)}
        title="Edit Metric Name"
        primaryAction={{
          content: "Save Changes",
          onAction: () => {
            handleRenameMetric(editingMetricName, editingMetricNewName);
            setShowEditMetricModal(false);
          },
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setShowEditMetricModal(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Metric name"
              value={editingMetricNewName}
              onChange={setEditingMetricNewName}
              autoComplete="off"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Modal to add metric */}
      <Modal
        open={showMetricModal}
        onClose={() => setShowMetricModal(false)}
        title="Add Metric"
        primaryAction={{
          content: "Add Metric",
          onAction: handleAddMetric,
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setShowMetricModal(false) },
        ]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Metric name"
              value={newMetricName}
              onChange={setNewMetricName}
              autoComplete="off"
              error={metricError}
            />
            <Select
              label="Metric type"
              options={[
                { label: "Text", value: "text" },
                { label: "Numerical", value: "numerical" },
                { label: "True/False", value: "boolean" },
                { label: "Percentage", value: "percentage" },
                { label: "Image", value: "image" }, // <-- added
              ]}
              value={newMetricType}
              onChange={setNewMetricType}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Modal to select competitor */}
      <Modal
        open={showCompetitorModal}
        onClose={() => setShowCompetitorModal(false)}
        title="Select Competitor"
        primaryAction={{
          content: "Add Competitor",
          onAction: handleConfirmAddCompetitor,
        }}
        secondaryActions={[
          { content: "Cancel", onAction: () => setShowCompetitorModal(false) },
        ]}
      >
        <Modal.Section>
          <Select
            label="Choose Competitor"
            options={competitorOptions.filter((o) => o.value)}
            value={selectedCompetitor}
            onChange={setSelectedCompetitor}
            placeholder="Please select"
          />
        </Modal.Section>
      </Modal>

      {isEditable && (
        <PageActions
          primaryAction={{
            content: "Save",
            onAction: handleSave,
            loading: loading,
          }}
        />
      )}
      {isImageUploading ? <LoadingScreen /> : null}

      {active && <Toast content={toastContent} onDismiss={toggleActive} />}
    </Page>
  ) : (
    <div className="spinnerContainer">
      <div className="vertical-center">
        <Spinner size="large" />
      </div>
    </div>
  );
}
